import type { AtomRecord } from "../model/atom.js";
import type { PathCandidateView } from "../model/evaluation.js";
import type { BoardState } from "../model/position.js";

type ProofSignal = {
  evidenceScore: number;
  proofCount: number;
};

export function derivePathCandidates(state: BoardState): PathCandidateView[] {
  const paths = Object.values(state.atoms)
    .filter((atom) => atom.kind === "path" && atom.status !== "archived" && atom.status !== "rejected")
    .sort((left, right) => left.id.localeCompare(right.id));
  const selectedPathId = state.line.selectedPathId;
  const activeGoals = Object.values(state.atoms).filter(isActiveGoal);
  const activeConstraints = Object.values(state.atoms).filter(isActiveConstraint);
  const threatTargets = collectThreatTargets(state);
  const proofSignals = collectProofSignals(state);

  return paths
    .map((path) =>
      scorePath(path, {
        activeConstraints,
        activeGoals,
        proofSignals,
        selectedPathId,
        state,
        threatTargets,
      }))
    .sort((left, right) =>
      right.score - left.score ||
      Number(right.selected) - Number(left.selected) ||
      left.pathId.localeCompare(right.pathId)
    );
}

function scorePath(
  path: AtomRecord,
  input: {
    activeConstraints: AtomRecord[];
    activeGoals: AtomRecord[];
    proofSignals: Map<string, ProofSignal>;
    selectedPathId: string | undefined;
    state: BoardState;
    threatTargets: Set<string>;
  },
): PathCandidateView {
  const supportedGoalIds = new Set(
    Object.values(input.state.cells)
      .filter((cell) => cell.status === "active" && cell.effect === "supports" && cell.from === path.id)
      .map((cell) => cell.to),
  );
  const attachedToolIds = new Set(
    Object.values(input.state.cells)
      .filter((cell) => cell.status === "active" && cell.effect === "uses" && cell.from === path.id)
      .map((cell) => cell.to),
  );
  const directThreats = countThreats(input.threatTargets, [path.id]);
  const coveredThreats = countThreats(
    input.threatTargets,
    input.activeGoals.filter((goal) => supportedGoalIds.has(goal.id)).map((goal) => goal.id),
  );
  const missingGoals = input.activeGoals.filter((goal) => !supportedGoalIds.has(goal.id));
  const unresolvedConstraints = input.activeConstraints.filter((constraint) =>
    !input.proofSignals.has(constraint.id)
  );
  const pathProof = input.proofSignals.get(path.id);
  const goalProofStrength = input.activeGoals
    .filter((goal) => supportedGoalIds.has(goal.id))
    .reduce((total, goal) => total + (input.proofSignals.get(goal.id)?.evidenceScore ?? 0), 0);

  let score = 60;
  const reasons: string[] = [];

  score += supportedGoalIds.size * 22;
  if (supportedGoalIds.size > 0) {
    reasons.push(`covers ${supportedGoalIds.size} goal${supportedGoalIds.size === 1 ? "" : "s"}`);
  }

  score -= missingGoals.length * 18;
  if (missingGoals.length > 0) {
    reasons.push(`missing ${missingGoals.length} goal link${missingGoals.length === 1 ? "" : "s"}`);
  }

  if (attachedToolIds.size > 0) {
    score += 14;
    reasons.push(`uses ${attachedToolIds.size} tool${attachedToolIds.size === 1 ? "" : "s"}`);
  } else {
    score -= 14;
    reasons.push("has no tool attachment");
  }

  if (pathProof !== undefined) {
    score += 12 + pathProof.evidenceScore;
    reasons.push(`has ${pathProof.proofCount} resolved proof${pathProof.proofCount === 1 ? "" : "s"}`);
  } else {
    score -= 10;
    reasons.push("has no resolved path proof");
  }

  if (goalProofStrength > 0) {
    const goalProofBonus = Math.min(18, goalProofStrength);
    score += goalProofBonus;
    reasons.push("supported goals have external evidence");
  }

  if (unresolvedConstraints.length > 0) {
    score -= unresolvedConstraints.length * 8;
    reasons.push(
      `${unresolvedConstraints.length} active constraint${unresolvedConstraints.length === 1 ? "" : "s"} still need proof`,
    );
  }

  if (directThreats > 0) {
    score -= directThreats * 28;
    reasons.push(`${directThreats} direct threat${directThreats === 1 ? "" : "s"} on the path`);
  }

  if (coveredThreats > 0) {
    score -= coveredThreats * 16;
    reasons.push(`${coveredThreats} threat${coveredThreats === 1 ? "" : "s"} on supported goals`);
  }

  if (input.selectedPathId === path.id) {
    score += 2;
    reasons.push("currently selected");
  }

  return {
    pathId: path.id,
    reasons,
    score,
    selected: input.selectedPathId === path.id,
    summary: `${path.id} score=${score}`,
  };
}

function collectThreatTargets(state: BoardState) {
  return new Set(
    Object.values(state.cells)
      .filter((cell) => cell.status === "active" && cell.effect === "threatens")
      .map((cell) => cell.to),
  );
}

function collectProofSignals(state: BoardState) {
  const signals = new Map<string, ProofSignal>();

  for (const cell of Object.values(state.cells)) {
    if (cell.status !== "active" || cell.effect !== "proves") {
      continue;
    }
    const proof = state.atoms[cell.from];
    if (proof?.kind !== "proof" || proof.status !== "resolved") {
      continue;
    }
    const existing = signals.get(cell.to) ?? { evidenceScore: 0, proofCount: 0 };
    existing.evidenceScore += inferEvidenceStrength(proof.body);
    existing.proofCount += 1;
    signals.set(cell.to, existing);
  }

  return signals;
}

function inferEvidenceStrength(body: string) {
  const text = body.toLowerCase();
  let score = 0;
  const signals = [
    /\b(agent-browser|browser|screenshot|snapshot)\b/,
    /\b(test|vitest|assert|benchmark)\b/,
    /\b(console|runtime|trace|log)\b/,
    /\b(build|lint|typecheck|tsc)\b/,
    /\b(http|https|file:|artifacts?)\b/,
  ];
  for (const pattern of signals) {
    if (pattern.test(text)) {
      score += 2;
    }
  }
  return score;
}

function countThreats(threatTargets: Set<string>, targetIds: readonly string[]) {
  return targetIds.reduce((total, targetId) => total + Number(threatTargets.has(targetId)), 0);
}

function isActiveGoal(atom: AtomRecord) {
  return atom.kind === "goal" && atom.status !== "archived" && atom.status !== "rejected";
}

function isActiveConstraint(atom: AtomRecord) {
  return atom.kind === "constraint" && atom.status !== "archived" && atom.status !== "rejected";
}
